import { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Loader2, Send } from "lucide-react";
import toast from "react-hot-toast";
import {
  FetchAdminInquiryMessages,
  SendAdminInquiryMessage,
  ClearAdminInquiryAction,
} from "../../Store/Actions/AdminInquiryActions";

const STATUS_STYLES = {
  OPEN: "bg-green-100 text-green-700",
  IN_PROGRESS: "bg-yellow-100 text-yellow-700",
  CLOSED: "bg-gray-100 text-gray-600",
};

const STATUS_LABELS = {
  OPEN: "Open",
  IN_PROGRESS: "In Progress",
  CLOSED: "Closed",
};

function formatTime(isoString) {
  if (!isoString) return "";
  return new Date(isoString).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export default function AdminInquiryDetail() {
  const { id } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { messages, loader, actionSuccess, actionError, inquiries } = useSelector(
    (state) => state.adminInquiry
  );

  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const messagesEndRef = useRef(null);
  const actionHandled = useRef(false);

  const inquiry = inquiries.find((inq) => inq.id === id) ?? null;

  useEffect(() => {
    dispatch(FetchAdminInquiryMessages(id));
    return () => dispatch(ClearAdminInquiryAction());
  }, [dispatch, id]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (actionSuccess && !actionHandled.current) {
      actionHandled.current = true;
      setSending(false);
      setReplyText("");
      dispatch(ClearAdminInquiryAction());
    }
  }, [actionSuccess, dispatch]);

  useEffect(() => {
    if (actionError && !actionHandled.current) {
      actionHandled.current = true;
      toast.error(actionError.message || "Failed to send reply.");
      setSending(false);
      dispatch(ClearAdminInquiryAction());
    }
  }, [actionError, dispatch]);

  function handleSend(e) {
    e.preventDefault();
    if (!replyText.trim()) return;
    actionHandled.current = false;
    setSending(true);
    dispatch(SendAdminInquiryMessage(id, replyText.trim()));
  }

  const isClosed = inquiry?.status === "CLOSED";

  return (
    <div className="p-6 lg:p-8 flex flex-col h-full">
      {/* Back + Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate("/dashboard/admin-inquiries")}
          className="flex items-center gap-2 text-gray-500 hover:text-gray-800 text-sm font-popinsMedium mb-4 transition"
        >
          <ArrowLeft size={16} />
          Back to Inquiries
        </button>
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <h1 className="text-xl font-popinsSemiBold text-gray-800 flex-1">
            {inquiry?.subject ?? "Inquiry"}
          </h1>
          {inquiry && (
            <span
              className={`inline-block text-xs font-popinsMedium px-3 py-1 rounded-full ${
                STATUS_STYLES[inquiry.status] ?? "bg-gray-100 text-gray-600"
              }`}
            >
              {STATUS_LABELS[inquiry.status] ?? inquiry.status}
            </span>
          )}
        </div>
      </div>

      {/* Messages */}
      {loader && messages.length === 0 ? (
        <div className="flex justify-center items-center py-20">
          <Loader2 size={28} className="animate-spin text-themeColor" />
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto space-y-4 mb-6 max-h-[calc(100vh-340px)] pr-1">
          {messages.map((msg) => (
            <div
              key={msg.id}
              className={`flex ${msg.isMine ? "justify-end" : "justify-start"}`}
            >
              <div
                className={`max-w-[75%] rounded-2xl px-4 py-3 shadow-sm ${
                  msg.isMine
                    ? "bg-themeColor text-white rounded-br-sm"
                    : "bg-white border border-gray-100 text-gray-800 rounded-bl-sm"
                }`}
              >
                {!msg.isMine && (
                  <p className="text-xs font-popinsSemiBold mb-1 text-themeColor">
                    {[msg.senderFirstName, msg.senderLastName].filter(Boolean).join(" ") || "Admin"}
                  </p>
                )}
                <p className="text-sm font-popinsRegular leading-relaxed whitespace-pre-wrap">
                  {msg.message}
                </p>
                <p
                  className={`text-xs mt-1.5 ${
                    msg.isMine ? "text-white/70" : "text-gray-400"
                  }`}
                >
                  {formatTime(msg.createdAt)}
                </p>
              </div>
            </div>
          ))}
          <div ref={messagesEndRef} />
        </div>
      )}

      {/* Reply Box */}
      {isClosed ? (
        <div className="bg-gray-50 border border-gray-200 rounded-xl px-5 py-3 text-sm font-popinsRegular text-gray-500 text-center">
          This inquiry has been closed. No further replies can be sent.
        </div>
      ) : (
        <form onSubmit={handleSend} className="flex gap-3 items-end">
          <textarea
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                handleSend(e);
              }
            }}
            placeholder="Type your message… (Enter to send, Shift+Enter for new line)"
            rows={2}
            className="flex-1 border border-gray-200 rounded-xl px-4 py-3 text-sm font-popinsRegular focus:outline-none focus:ring-2 focus:ring-themeColor resize-none"
          />
          <button
            type="submit"
            disabled={sending || !replyText.trim()}
            className="flex items-center justify-center w-10 h-10 rounded-xl bg-themeColor text-white hover:bg-opacity-90 transition disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 mb-0.5"
          >
            {sending ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Send size={16} />
            )}
          </button>
        </form>
      )}
    </div>
  );
}
